/* eslint-disable no-nested-ternary */
import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import startCase from 'lodash/startCase';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import Checkbox from '@mui/material/Checkbox';
import TablePagination from '@mui/material/TablePagination';
import MKTable from 'components/MaterialKit/MKTable';
import MKBox from 'components/MaterialKit/MKBox';
import AnchorMenu from 'components/AnchorMenu';
import Button from 'components/Button';
import { withProps } from 'utils/hoc';
import { getLocaleMap } from 'utils/locales';
import { useTranslation } from 'react-i18next';
import InputField from 'components/InputField';
import TablePageActionButtonGroup from './TablePageActionButtonGroup';

const DelayedInputField = ({onChangeValue, value, ...props}) => {
  const [intermediateValue, setIntermediateValue] = useState(value);
  const [prevValue, setPrevValue] = useState(value);
  if (prevValue !== value) {
    setIntermediateValue(value);
    setPrevValue(value);
  }
  const handleValueChange = (newValue) => {
    onChangeValue(newValue);
  }
  return <InputField
    value={intermediateValue}
    onKeyUp={(e) => {
      if (e.code === 'Enter') {
        handleValueChange(intermediateValue);
      }
    }}
    onBlur={(e) => {
      handleValueChange(intermediateValue);
    }}
    onChange={(e) => {
      setIntermediateValue(e.target.value);
    }}
    {...props}
  />
}

const DataTable = ({ columns, data, showHeader, colorDataMap, idField, selectable, objectTypes, typeRouting, onSelectRow, onClickRow, onPressAdd, onPressEdit, onPressDelete, onPressCopy, onPressPaste, totalCount, currentPage, onPageChange, rowsPerPage, onRowsPerPageChange, rowsPerPageOptions, paginationDisabled, sortBy, sortDirection, onSort, pageButtonProps, addButtonLabel, editButtonLabel, deleteButtonLabel, copyButtonLabel, pasteButtonLabel, headerBackgroundColor, headerFontColor, oddRowBackgroundColor, oddRowFontColor, evenRowBackgroundColor, evenRowFontColor, headerFontWeight, bodyFontWeight, rowFontSize, ...props }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [localeMapping, setLocaleMapping] = useState({});
  const navigate = useNavigate();
  const { t } = useTranslation('Portal');

  useEffect(() => {
    getLocaleMap(['add_button', 'edit_button', 'delete_button', 'confirmation_prompts_delete', 'copy_button', 'paste_button',
      'labels_to', 'labels_of', 'labels_more_than', 'labels_page_size'])
      .then((response) => {
        setLocaleMapping(response);
      });
  }, []);

  const handlePageChange = (newValue) => {
    const newCurrentPageInput = Number(newValue);
    if (Number.isSafeInteger(newCurrentPageInput)) {
      const newCurrentPage = newCurrentPageInput > 0 ? newCurrentPageInput - 1 : 0;
      onPageChange(newCurrentPage);
    }
  }

  const onOpenMenu = useCallback(({ currentTarget }) => setMenuAnchor(currentTarget), []);

  const selectedItem = useMemo(() => (data || []).find(
    (datum) => datum[idField] === selectedIds[0],
  ), [data, idField, selectedIds]);

  const headers = useMemo(() => [
    ...(selectable ? [{
      name: 'selected',
      label: '',
      width: '0',
    }] : []),
    ...columns.map((column, i) => {
      const { label, field, ...colProps } = column;
      let arrowIcon;

      if (sortDirection === 'asc') {
        arrowIcon = 'arrow_drop_up';
      } else if (sortDirection === 'desc') {
        arrowIcon = 'arrow_drop_down';
      }
      return {
        name: field,
        label: (
          <Grid container justifyContent={i > 0 ? 'center' : 'flex-start'} alignItems="center" flexWrap="nowrap">
            {label !== undefined ? t(`${label}`) ? t(`${label}`) : label : startCase(t(`${field}`))}
            {field === sortBy && (
              <Icon fontSize="default" color="dark">
                {arrowIcon}
              </Icon>
            )}
          </Grid>
        ),
        align: i > 0 ? 'center' : 'left',
        ...colProps,
      };
    }),
  ], [columns, selectable, sortBy, sortDirection, t]);

  const rows = useMemo(() => (data || []).map((datum) => {
    return columns.reduce((rowData, column) => {
      const { field, to, urlParams, href, formatter, content, renderContent, onClick } = column;
      const updatedRowData = { ...rowData };
      const displayText = formatter ? formatter(get(datum, field)) : get(datum, field);

      const parsedTo = (urlParams || []).reduce((result, urlParam) => {
        return result.replaceAll(`:${urlParam}`, get(datum, urlParam));
      }, to);

      if (typeof renderContent === 'function') {
        updatedRowData[field] = renderContent(datum);
      } else if (content) {
        updatedRowData[field] = content;
      } else if (to || href || onClick) {
        updatedRowData[field] = (
          <Button
            variant="text"
            onClick={() => (typeof onClick === 'function' ? onClick(datum) : null)}
            to={parsedTo}
            href={href}
            fullWidth
          >
            {displayText}
          </Button>
        );
      } else {
        updatedRowData[field] = displayText;
      }
      if (selectable) {
        updatedRowData.selected = (
          <Checkbox
            checked={(selectedIds || []).includes(datum[idField])}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onSelectRow) {
                onSelectRow(datum[idField], e.target.checked);
              }
              if (e.target.checked) {
                setSelectedIds([...selectedIds, datum[idField]]);
              } else {
                setSelectedIds(selectedIds.filter((id) => id !== datum[idField]));
              }
            }}
          />
        );
      }
      return updatedRowData;
    }, {});
  }), [data, columns, selectable, selectedIds, idField, onSelectRow]);

  const actionButtons = useMemo(() => [
    ...(typeof onPressAdd === 'function' ? [{
      label: localeMapping[addButtonLabel] || addButtonLabel || localeMapping.add_button || 'Add',
      color: 'success',
      onClick: objectTypes.length > 0 ? onOpenMenu : () => onPressAdd('default'),
    }] : []),
    // ...(typeof onPressEdit === 'function' ? [{
    //   label: localeMapping[editButtonLabel] || editButtonLabel || localeMapping.edit_button || 'Edit',
    //   color: 'info',
    //   disabled: selectedIds.length === 0 || selectedIds.length > 1,
    //   to: typeRouting[selectedItem?.type] || typeRouting?.default,
    //   onClick: () => onPressEdit(selectedIds[0]),
    // }] : []),
    ...(typeof onPressDelete === 'function' ? [{
      label: localeMapping[deleteButtonLabel] || deleteButtonLabel || localeMapping.delete_button || 'Delete',
      color: 'error',
      disabled: selectedIds.length === 0,
      onClick: () => {
        if (window.confirm(localeMapping.confirmation_prompts_delete || 'Are you sure you want to delete this item?')) {
          return Promise.all(selectedIds.map((id) => onPressDelete(id)))
            .then(() => setSelectedIds([]));
        }
        return Promise.resolve();
      },
    }] : []),
    ...(typeof onPressCopy === 'function' ? [{
      label: localeMapping.copy_button || copyButtonLabel || 'Copy',
      color: 'dark',
      disabled: !selectedIds.length === 0,
      onClick: () => onPressCopy(selectedIds[0]),
    }] : []),
    ...(typeof onPressPaste === 'function' ? [{
      label: localeMapping.paste_button || pasteButtonLabel || 'Paste',
      color: 'secondary',
      onClick: onPressPaste,
    }] : []),
  ], [onPressAdd, localeMapping, addButtonLabel, objectTypes.length, onOpenMenu, onPressEdit, editButtonLabel, selectedIds, typeRouting, selectedItem?.type, onPressDelete, deleteButtonLabel, onPressCopy, copyButtonLabel, onPressPaste, pasteButtonLabel]);

  const objectTypeOptions = useMemo(() => objectTypes.map((type) => ({
    label: type.name,
    value: type.object_type_id,
  })), [objectTypes]);

  return (
    <MKBox width="100%">
      {actionButtons.length > 0 && (
        <MKBox display="flex" flexDirection="row" pb={1}>
          {actionButtons.map((actionButton, idx) => {
            const { label, color, onClick, to, disabled } = actionButton;
            return (
              <MKBox ml={idx > 0 ? 1 : 0}>
                <Button
                  size="small"
                  variant="gradient"
                  color={color}
                  onClick={onClick}
                  to={to}
                  disabled={disabled}
                >
                  {label}
                </Button>
              </MKBox>
            );
          })}
        </MKBox>
      )}
      <MKTable
        idField={idField}
        columns={headers}
        rows={rows}
        showHeader={showHeader}
        colorDataMap={colorDataMap}
        onClickHeader={onSort}
        getRowProps={typeof onClickRow === 'function' ? (row, idx) => ({
          hover: true,
          sx: { cursor: 'pointer' },
          onClick: () => {
            const clickedItem = data[idx];
            return Promise.resolve(onClickRow(clickedItem[idField]))
              .then(() => {
                const targetRoute = typeRouting[clickedItem?.type] || typeRouting?.default;
                if (targetRoute) {
                  navigate(typeRouting[clickedItem?.type] || typeRouting?.default);
                }
              });
          },
        }) : null}
        headerBackgroundColor={headerBackgroundColor}
        headerFontColor={headerFontColor}
        oddRowBackgroundColor={oddRowBackgroundColor}
        oddRowFontColor={oddRowFontColor}
        evenRowBackgroundColor={evenRowBackgroundColor}
        evenRowFontColor={evenRowFontColor}
        headerFontWeight={headerFontWeight}
        bodyFontWeight={bodyFontWeight}
        rowFontSize={rowFontSize}
      />
      {!paginationDisabled && (
        <MKBox display="flex" flexWrap="wrap" rowGap={1} alignItems="center" justifyContent="center" mt={1}>
          <TablePagination
            component="div"
            showFirstButton
            showLastButton
            count={totalCount}
            page={currentPage}
            onPageChange={(e, page) => onPageChange(page)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => onRowsPerPageChange(e.target.value)}
            rowsPerPageOptions={rowsPerPageOptions}
            labelDisplayedRows={({ from, to, count, page }) => `${from} ${localeMapping.labels_to} ${to} ${localeMapping.labels_of} ${count !== -1 ? count : `${localeMapping.labels_more_than} ${to}`}`}
            labelRowsPerPage={localeMapping.labels_page_size}
            ActionsComponent={withProps(TablePageActionButtonGroup, { buttonProps: pageButtonProps })}
            sx={{
              '.MuiTablePagination-spacer': {
                display: 'none',
              },
              // The default toolbar tries to fit the rows-per-page select,
              // the "x to y of z" label, and the First/Prev/Page/Next/Last
              // buttons in one non-wrapping row, which overflows a mobile
              // viewport and causes overlapping text. Let it wrap instead.
              '.MuiTablePagination-toolbar': {
                flexWrap: 'wrap',
                justifyContent: 'center',
                rowGap: 1,
                height: 'auto',
                minHeight: 'auto',
                padding: '0.5rem 0',
              },
              '.MuiTablePagination-spacer + .MuiTablePagination-selectLabel': {
                marginLeft: 0,
              },
            }}
          />
          <MKBox flexShrink={0} width="70px">
            <DelayedInputField
              label=""
              value={currentPage + 1}
              onChangeValue={handlePageChange}
            />
          </MKBox>
        </MKBox>
      )}
      <AnchorMenu
        items={objectTypeOptions}
        onClickItem={onPressAdd}
        anchor={menuAnchor}
        setAnchor={setMenuAnchor}
      />
    </MKBox>
  );
};

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      field: PropTypes.string,
      to: PropTypes.string,
      href: PropTypes.string,
      urlParams: PropTypes.arrayOf(PropTypes.string),
      formatter: PropTypes.func,
      content: PropTypes.node,
      renderContent: PropTypes.func,
      onClick: PropTypes.func,
    }),
  ),
  data: PropTypes.arrayOf(PropTypes.object),
  showHeader: PropTypes.bool,
  colorDataMap: PropTypes.arrayOf(PropTypes.object),
  idField: PropTypes.string,
  selectable: PropTypes.bool,
  objectTypes: PropTypes.object,
  typeRouting: PropTypes.object,
  onSelectRow: PropTypes.func,
  onClickRow: PropTypes.func,
  onPressAdd: PropTypes.func,
  onPressEdit: PropTypes.func,
  onPressDelete: PropTypes.func,
  onPressCopy: PropTypes.func,
  onPressPaste: PropTypes.func,
  totalCount: PropTypes.number,
  currentPage: PropTypes.number,
  onPageChange: PropTypes.func,
  rowsPerPage: PropTypes.number,
  onRowsPerPageChange: PropTypes.func,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  paginationDisabled: PropTypes.bool,
  sortBy: PropTypes.string,
  sortDirection: PropTypes.string,
  onSort: PropTypes.func,
  pageButtonProps: PropTypes.object,
  addButtonLabel: PropTypes.string,
  editButtonLabel: PropTypes.string,
  deleteButtonLabel: PropTypes.string,
  copyButtonLabel: PropTypes.string,
  pasteButtonLabel: PropTypes.string,
  headerBackgroundColor: PropTypes.string,
  headerFontColor: PropTypes.string,
  oddRowBackgroundColor: PropTypes.string,
  oddRowFontColor: PropTypes.string,
  evenRowBackgroundColor: PropTypes.string,
  evenRowFontColor: PropTypes.string,
  headerFontWeight: PropTypes.string,
  bodyFontWeight: PropTypes.string,
  rowFontSize: PropTypes.string,
};

DataTable.defaultProps = {
  columns: [],
  data: [],
  showHeader: true,
  colorDataMap: [],
  idField: 'id',
  selectable: false,
  objectTypes: [],
  typeRouting: {},
  onSelectRow: null,
  onClickRow: null,
  onPressAdd: null,
  onPressEdit: null,
  onPressDelete: null,
  onPressCopy: null,
  onPressPaste: null,
  totalCount: 0,
  currentPage: 0,
  onPageChange: () => {},
  rowsPerPage: 10,
  onRowsPerPageChange: () => {},
  rowsPerPageOptions: [5, 10, 15, 20],
  paginationDisabled: false,
  sortBy: null,
  sortDirection: null,
  onSort: () => {},
  pageButtonProps: {},
  addButtonLabel: 'Add',
  editButtonLabel: 'Edit',
  deleteButtonLabel: 'Delete',
  copyButtonLabel: 'Copy',
  pasteButtonLabel: 'Paste',
  headerBackgroundColor: '',
  headerFontColor: '',
  oddRowBackgroundColor: '',
  oddRowFontColor: '',
  evenRowBackgroundColor: '',
  evenRowFontColor: '',
  headerFontWeight: '',
  bodyFontWeight: '',
  rowFontSize: '',
};

export default DataTable;
